import { ChangeEvent, useState } from 'react';
import FormField from '@/common/PassengerTools/FormField';
import { Button as StyledButton, Select } from '../PassengerTools.styles';
import { addSoftPullToCurrentUser } from '../utils/createTestUser';

type OwnProps = {
  creditStatus: 'Excellent' | 'Fair';
  expired: boolean;
  isLoggedIn: boolean;
  isOpen: boolean;
  onCreditStatusChange: (status: 'Excellent' | 'Fair') => void;
  onExpiredChange: (expired: boolean) => void;
  onOpenChange: (open: boolean) => void;
};

export default function SoftPullSection({
  creditStatus,
  expired,
  isLoggedIn,
  isOpen,
  onCreditStatusChange,
  onExpiredChange,
  onOpenChange
}: OwnProps) {
  const [errorMessage, setErrorMessage] = useState('');
  const [isAdding, setIsAdding] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  async function handleAddSoftPull() {
    setIsAdding(true);
    setErrorMessage('');
    setSuccessMessage('');
    try {
      await addSoftPullToCurrentUser({ creditStatus, expired });
      setSuccessMessage(
        `Soft pull applied successfully${expired ? ' (expired)' : ''}.`
      );
    } catch (error) {
      setErrorMessage(`Failed to apply soft pull: ${error}`);
    } finally {
      setIsAdding(false);
    }
  }

  const label = isLoggedIn
    ? 'Add Soft Pull'
    : 'Add Soft Pull (Must be logged in)';

  return (
    <div className="flex flex-col items-start">
      <button
        type="button"
        onClick={() => isLoggedIn && onOpenChange(!isOpen)}
        disabled={!isLoggedIn}
        className="inline-flex items-center gap-8 rounded bg-gray-600 px-lg py-md text-white hover:bg-gray-700 disabled:cursor-not-allowed disabled:opacity-50"
        data-analytics-id={null}
        data-testid="toggle-soft-pull"
      >
        <span className="text-sm font-medium">{label}</span>
        {successMessage && !isOpen && (
          <span className="text-xs text-green-400">&#10003; Applied</span>
        )}
        <span className="text-sm">{isOpen ? '▾' : '▸'}</span>
      </button>

      {isOpen && (
        <div
          className={`self-stretch flex flex-col gap-8 border border-gray-600 rounded-b p-sm${
            !isLoggedIn ? ' opacity-50 pointer-events-none' : ''
          }`}
        >
          <FormField label="Credit Status" htmlFor="softpull-credit-status">
            <Select
              id="softpull-credit-status"
              value={creditStatus}
              onChange={(e: ChangeEvent<HTMLSelectElement>) =>
                onCreditStatusChange(e.target.value as 'Excellent' | 'Fair')
              }
            >
              <option value="Excellent">Excellent</option>
              <option value="Fair">Fair</option>
            </Select>
          </FormField>

          <div className="flex items-center gap-8">
            <input
              type="checkbox"
              id="softpull-expired"
              checked={expired}
              onChange={e => onExpiredChange(e.target.checked)}
              className="h-16 w-16"
            />
            <label htmlFor="softpull-expired" className="text-white">
              Expired
            </label>
          </div>

          {successMessage && (
            <div className="text-green-400 text-sm">{successMessage}</div>
          )}
          {errorMessage && (
            <div className="text-[#feaaaa] text-sm">{errorMessage}</div>
          )}

          <StyledButton
            type="button"
            onClick={handleAddSoftPull}
            data-analytics-id={null}
            disabled={isAdding}
            data-testid="add-soft-pull"
          >
            {isAdding ? 'Applying...' : 'Apply Soft Pull'}
          </StyledButton>
        </div>
      )}
    </div>
  );
}
